import moment from "moment";
import { useEffect, useState } from "react";
import "react-quill/dist/quill.snow.css"; // Import Quill styles
import { getVessels } from "../../utils/ApiCalls/SpecialPage";
import { getDailyPlannerData, addExtraDays } from "../../utils/ApiCalls/DailyPlanner";
import { addlog } from "../../utils/ApiCalls/Logs";
import Swal from "sweetalert2";
import { useDataLayerValue } from "../../DataLayer/DataLayer";
import { Divider } from "antd";
import { DAILY_PLANNER_LOG_TYPES, createPlannerTitle } from "../../constants/logging/dailyPlanner";
import CruisePageSelector from "./CruisePageSelector";
import DailyPlannerPageSelector from "./DailyPlannerPageSelector";

const DailyPlannerDataInput = ({ newsletter }) => {
  const [vesselOption, setVesselOptions] = useState([]);
  const [extraDuration, setExtraDuration] = useState(0);
  const [dailyPlannerId, setDailyPlannerId] = useState(null);
  const [dailyPlannerData, setDailyPlannerData] = useState(null);
  const [scheduleRemovalFunction, setScheduleRemovalFunction] = useState(null);
  const [{ userDetails }, dispatch] = useDataLayerValue();

  useEffect(() => {
    getOptionLocation();
  }, []);

  useEffect(() => {
    if (newsletter?.id && !dailyPlannerData) {
      fetchDailyPlannerData();
    }
  }, [newsletter?.id, dailyPlannerData]);

  const fetchDailyPlannerData = async () => {
    try {
      const data = await getDailyPlannerData({ 
        id: newsletter.id 
      });
      if (data?.data?.length > 0) {
        const plannerData = data.data[0];
        setExtraDuration(plannerData.extraDuration || 0);
        setDailyPlannerId(plannerData.id);
        setDailyPlannerData(plannerData);
      }
    } catch (error) {
      console.error("Error fetching daily planner data:", error);
    }
  };
  const getOptionLocation = async () => {
    dispatch({
      type: "SET_LOADING",
      loading: true,
    });

    let vessels = await getVessels();
    if (vessels.error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: vessels.error,
      });
    } else {
      setVesselOptions(vessels);
    }
    dispatch({
      type: "SET_LOADING",
      loading: false,
    });
  };

  const handleExtraDays = async (operation) => {
    if (!dailyPlannerId) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No daily planner found for this sailing",
      });
      return;
    }

    if (operation === 'remove') {
      Swal.fire({
        title: 'Are you sure?',
        text: 'Are you sure you want to remove extra night? It will also delete any scheduled activities on that day.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, remove it!'
      }).then(async (result) => {
        if (result.isConfirmed) {
          await updateExtraDays(extraDuration - 1);
        }
      });
      return;
    }

    // Handle add operation
    await updateExtraDays(extraDuration + 1);
  };

  const updateExtraDays = async (newExtraDays) => {
    try {
      dispatch({
        type: "SET_LOADING",
        loading: true,
      });

      // If reducing extra days, handle scheduled activity removal using child component function
      if (newExtraDays < extraDuration && scheduleRemovalFunction) {
        const removalResult = await scheduleRemovalFunction(newExtraDays);
        if (!removalResult.success) {
          console.error("Failed to remove scheduled activities:", removalResult.error);
          // Continue with the operation but log the error
        } else if (removalResult.deletedCount) {
          console.log(`Successfully removed ${removalResult.deletedCount} scheduled activities`);
        }
      }

      const result = await addExtraDays({
        dailyPlannerId: dailyPlannerId,
        extraDays: newExtraDays
      });

      if (result.error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: result.error,
        });
      } else {
        // Reload data from API to get updated information
        await fetchDailyPlannerData();
        
        const vesselName = vesselOption.find(v => v.id === newsletter?.vessel_id)?.name || "Unknown Vessel";
        const plannerTitle = createPlannerTitle(newsletter?.sailing_date, newsletter?.duration);
        const isAdding = newExtraDays > extraDuration;
        
        await addlog(
          isAdding ? DAILY_PLANNER_LOG_TYPES.ACTIONS.EXTRA_NIGHT_ADDED : DAILY_PLANNER_LOG_TYPES.ACTIONS.EXTRA_NIGHT_REMOVED,
          DAILY_PLANNER_LOG_TYPES.MODULE,
          {
            title: `Extra night ${isAdding ? 'added to' : 'removed from'} ${plannerTitle}`,
            action: isAdding ? DAILY_PLANNER_LOG_TYPES.ACTION_TYPES.EXTRA_NIGHT_ADD : DAILY_PLANNER_LOG_TYPES.ACTION_TYPES.EXTRA_NIGHT_REMOVE,
            planner_title: plannerTitle,
            planner_id: newsletter?.id,
            daily_planner_id: dailyPlannerId,
            previous_extra_nights: extraDuration,
            new_extra_nights: newExtraDays,
            vessel_name: vesselName,
            sailing_date: newsletter?.sailing_date
          },
          userDetails.id
        );
        
        Swal.fire({
          icon: "success",
          title: "Success",
          text: `Extra night ${isAdding ? 'added' : 'removed'} successfully`,
          timer: 1500,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update extra days",
      });
    } finally {
      dispatch({
        type: "SET_LOADING",
        loading: false,
      });
    }
  };
  
  const handleScheduleRemovalReady = (removalFunction) => {
    setScheduleRemovalFunction(() => removalFunction);
  };
  
  return (
    <div>
      <section className="content">
        <div className="container-fluid">
          <div className="card">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                background: "lightgray",
                justifyContent:"center",
                height:"50px",
                position: "relative"
              }}
            >
              <p>
                {
                  vesselOption.find((item) => item.id === newsletter?.vessel_id)
                    ?.name
                }{" "}
                - {moment(newsletter?.startDate).format("DD MMM")} to {moment(newsletter?.endDate).format("DD MMM YYYY")} |
                {" " + newsletter?.duration} Nights
                {extraDuration > 0 && (
                  <span>
                    {" "}+ {extraDuration} Extra Night{extraDuration > 1 ? 's' : ''}
                  </span>
                )}
              </p>
              
              {/* Extra Duration Control Box */}
              <div style={{
                position: "absolute",
                right: "20px",
                top: "50%",
                transform: "translateY(-50%)",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}>
                <button 
                  className="btn" 
                  style={{
                    backgroundColor: "#000",
                    color: "white",
                    border: "none",
                    borderRadius: "3px",
                    padding: "4px 8px",
                    fontSize: "11px",
                    cursor: "pointer"
                  }}
                  onClick={() => handleExtraDays('add')}
                  title="Add Extra Night"
                >
                  Add Extra Night
                </button>
                {extraDuration > 0 && (
                  <button 
                    className="btn" 
                    style={{
                      backgroundColor: "#dc3545",
                      color: "white",
                      border: "none",
                      borderRadius: "3px",
                      padding: "4px 8px",
                      fontSize: "11px",
                      cursor: "pointer"
                    }}
                    onClick={() => handleExtraDays('remove')}
                    title="Remove Extra Night"
                  >
                    Remove Extra Night
                  </button>
                )}
              </div>
            </div>
                
           {newsletter?.vessel_id && vesselOption?.length > 0 && dailyPlannerData &&  <DailyPlannerPageSelector  
              vessel_id={newsletter?.vessel_id}
              sailing_date={newsletter?.sailing_date}
              vesselOptions={vesselOption}
              sailing_id={newsletter?.id}
              sailing_page={newsletter}
              dailyPlannerData={dailyPlannerData}
              onScheduleRemovalReady={handleScheduleRemovalReady}
            />}
          </div>
          <div className="pt-3"></div>
        </div>
      </section>
    </div>
  );
};

export default DailyPlannerDataInput;
